<script setup lang="ts">
  import { Picture, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed } from 'vue';
  import { type AppDownloadStore } from '@/shared/model';
  import FooterAppBadges from './logos/FooterAppBadges.vue';
  import FooterBrandLogo from './logos/FooterBrandLogo.vue';
  import FooterPaymentLogos from './logos/FooterPaymentLogos.vue';
  import FooterSkolkovoBadge from './logos/FooterSkolkovoBadge.vue';
  import FooterSocialIcons from './logos/FooterSocialIcons.vue';
  import { BUY_TOUR_DEFAULT } from '../config/footerPages';
  import { FOOTER_LEGAL_LINKS, FOOTER_USEFUL_LINKS, type FooterLink, type FooterLinkAction } from '../config/links';
  import { FOOTER_TEXTS } from '../config/texts';
  import type { BuyTourSeo } from '../model/types';

  interface FooterProps {
    usefulLinks?: FooterLink[];
    legalLinks?: FooterLink[];
    phone?: string;
    phoneHref?: string;
    buyTourSeo?: BuyTourSeo;
    visibleStorages?: AppDownloadStore[];
  }

  const props = withDefaults(defineProps<FooterProps>(), {
    usefulLinks: () => FOOTER_USEFUL_LINKS,
    legalLinks: () => FOOTER_LEGAL_LINKS,
    phone: FOOTER_TEXTS.phone,
    phoneHref: 'tel:8 (800) 301-73-11',
    buyTourSeo: () => BUY_TOUR_DEFAULT,
    visibleStorages: () => ['appstore', 'playstore'],
  });

  interface FooterEmits {
    (e: 'navigate', action: FooterLinkAction): void;
    (e: 'download', action: AppDownloadStore): void;
  }
  defineEmits<FooterEmits>();

  const resolvedUsefulLinks = computed<FooterLink[]>(() =>
    props.usefulLinks.flatMap((link) => {
      if (link.action !== 'buyTour') {
        return [link];
      }
      return props.buyTourSeo ? [{ ...link, text: props.buyTourSeo.linkTitle }] : [];
    }),
  );
</script>

<template>
  <footer
    class="fr-footer"
    data-testid="footer"
  >
    <div class="fr-footer__main">
      <div class="fr-footer__content">
        <div class="fr-footer__links">
          <section
            class="fr-footer__section"
            data-testid="footer-useful"
          >
            <Typo
              :variant="TypoVariant.Heading5"
              tag="span"
              class="fr-footer__heading"
            >
              {{ FOOTER_TEXTS.headings.useful }}
            </Typo>
            <ul class="fr-footer__link-list">
              <li
                v-for="link in resolvedUsefulLinks"
                :key="link.text"
              >
                <button
                  v-if="link.action"
                  type="button"
                  class="fr-footer__link fr-footer__link_button"
                  data-testid="footer-link"
                  @click="$emit('navigate', link.action)"
                >
                  <Typo
                    :variant="TypoVariant.BodyMRegular"
                    tag="span"
                  >
                    {{ link.text }}
                  </Typo>
                </button>
                <a
                  v-else-if="link.href"
                  class="fr-footer__link"
                  :href="link.href"
                  target="_self"
                  data-testid="footer-link"
                >
                  <Typo
                    :variant="TypoVariant.BodyMRegular"
                    tag="span"
                  >
                    {{ link.text }}
                  </Typo>
                </a>
              </li>
            </ul>
          </section>

          <div class="fr-footer__partners">
            <section
              class="fr-footer__group"
              data-testid="footer-partners"
            >
              <Typo
                :variant="TypoVariant.Heading5"
                tag="span"
                class="fr-footer__heading"
              >
                {{ FOOTER_TEXTS.headings.partners }}
              </Typo>
              <FooterPaymentLogos class="fr-footer__payment" />
            </section>

            <section
              class="fr-footer__group fr-footer__group_social"
              data-testid="footer-social"
            >
              <Typo
                :variant="TypoVariant.Heading5"
                tag="span"
                class="fr-footer__heading"
              >
                {{ FOOTER_TEXTS.headings.social }}
              </Typo>
              <FooterSocialIcons class="fr-footer__social" />
            </section>

            <section
              class="fr-footer__group"
              data-testid="footer-app"
            >
              <Typo
                :variant="TypoVariant.Heading5"
                tag="span"
                class="fr-footer__heading"
              >
                {{ FOOTER_TEXTS.headings.app }}
              </Typo>
              <FooterAppBadges
                class="fr-footer__app"
                :visible-storages="visibleStorages"
                @download="$emit('download', $event)"
              />
            </section>
          </div>
        </div>

        <div class="fr-footer__about">
          <FooterBrandLogo class="fr-footer__logo" />

          <a
            class="fr-footer__phone"
            :href="phoneHref || undefined"
            data-testid="footer-phone"
          >
            <Picture
              path="travelata/clock.svg"
              :lazy="false"
              :extensions="[]"
              :high-resolution="false"
              fit="contain"
              class="fr-footer__phone-img"
            />
            <span class="fr-footer__phone-text">
              <Typo
                :variant="TypoVariant.BodyLMedium"
                tag="span"
              >
                {{ phone }}
              </Typo>
              <Typo
                :variant="TypoVariant.BodyLRegular"
                tag="span"
                class="fr-footer__phone-schedule"
              >
                {{ FOOTER_TEXTS.schedule }}
              </Typo>
            </span>
          </a>

          <Typo
            :variant="TypoVariant.BodyMRegular"
            tag="p"
            class="fr-footer__text"
          >
            {{ FOOTER_TEXTS.description }}
          </Typo>
          <Typo
            :variant="TypoVariant.BodyMRegular"
            tag="p"
            class="fr-footer__text"
          >
            {{ FOOTER_TEXTS.copyright }}
          </Typo>
        </div>

        <section
          class="fr-footer__skolkovo"
          data-testid="footer-skolkovo"
        >
          <Typo
            :variant="TypoVariant.Heading5"
            tag="span"
            class="fr-footer__heading"
          >
            {{ FOOTER_TEXTS.headings.skolkovo }}
          </Typo>
          <FooterSkolkovoBadge class="fr-footer__skolkovo-badge" />
        </section>
      </div>

      <ul
        class="fr-footer__legal"
        data-testid="footer-legal"
      >
        <li
          v-for="link in legalLinks"
          :key="link.text"
        >
          <a
            class="fr-footer__link"
            :href="link.href || undefined"
            :target="link.newTab ? '_blank' : undefined"
            :rel="link.newTab ? 'noopener noreferrer' : undefined"
            data-testid="footer-link"
          >
            <Typo
              :variant="TypoVariant.BodyMRegular"
              tag="span"
            >
              {{ link.text }}
            </Typo>
          </a>
        </li>
      </ul>
    </div>
  </footer>
</template>

<style scoped lang="scss">
  .fr-footer {
    background: var(--color-bg-inverse);
    box-sizing: border-box;
    color: var(--color-text-tertiary);
    padding: var(--spacing-xxl) var(--spacing-l) var(--spacing-xl);
    width: 100%;

    &__main {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxxl);
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xl);
    }

    &__phone-img {
      height: 40px;
      width: 40px;
    }

    &__links {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xl);
    }

    &__section {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
    }

    &__heading {
      color: var(--color-text-tertiary);
      display: block;
    }

    &__link-list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-s);
      list-style: none;
      margin: 0;
      padding: 0;
    }

    &__link {
      color: var(--color-text-inverse);
      cursor: pointer;
      display: inline-block;
      text-decoration: none;

      &_button {
        appearance: none;
        background: none;
        border: none;
        font: inherit;
        padding: 0;
        text-align: left;
      }

      &:hover {
        text-decoration: underline;
      }
    }

    &__partners {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxxl);
    }

    &__group {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-l);

      &_social {
        gap: 14px;
      }
    }

    &__about {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
    }

    &__logo {
      width: 200px;
    }

    &__phone {
      align-items: center;
      color: var(--color-text-tertiary);
      display: inline-flex;
      gap: var(--spacing-xs);
      text-decoration: none;
      width: fit-content;
    }

    &__phone-text {
      display: flex;
      flex-direction: column;
    }

    &__phone-schedule {
      color: var(--color-text-tertiary);
    }

    &__text {
      color: var(--color-text-tertiary);
      gap: var(--spacing-xs);
      margin: 0;
    }

    &__skolkovo {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
    }

    &__legal {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-s);
      list-style: none;
      margin: 0;
      padding: 0;
    }
  }
</style>
